import React from "react";

/**
 * ScrollableTable Component
 * Provides a scrollable data table with built-in search
 * for consistent use across all pages.
 *
 * IMPORTANT: this file was updated to be defensive when `data`
 * is undefined (happened earlier when API/mocks were missing).
 */

/**
 * ScrollableTable Component
 * Provides a virtualized or scrollable data table with built-in search and filtering.
 * 
 * @param {string} title - Table title.
 * @param {Array} columns - Column definitions { header, accessor, render }.
 * @param {Array} data - Array of data objects.
 * @param {string} searchPlaceholder - Placeholder text for search input.
 * @param {Function} onRowClick - Handler for row click events.
 * @param {ReactNode} filterContent - Optional custom filter controls next to search.
 * @param {number} height - Max height of the table body in pixels.
 */
export default function ScrollableTable({
  title,
  columns = [],
  data = [],
  searchPlaceholder = "Search...",
  onRowClick,
  filterContent,
  sortConfig: externalSortConfig,
  onSort: externalOnSort,
  searchQuery, // New prop for external control
  onSearch,    // New prop for external control
  hideSearch = false,
  height = 480,
}) {
  const [internalQuery, setInternalQuery] = React.useState("");
  const [internalSortConfig, setInternalSortConfig] = React.useState({ key: null, direction: 'asc' });

  const query = onSearch ? searchQuery : internalQuery;
  const sortConfig = externalSortConfig || internalSortConfig;

  // Make sure `data` is always an array
  const safeData = Array.isArray(data) ? data : [];

  const filteredData = React.useMemo(() => {
    // If search is handled externally, we assume `data` is already filtered.
    if (onSearch) return safeData;

    return safeData.filter((item) => {
      const q = query.toLowerCase();
      try {
        return Object.values(item).some((val) =>
          String(val).toLowerCase().includes(q)
        );
      } catch (e) {
        return false;
      }
    });
  }, [safeData, query, onSearch]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    if (onSearch) {
      onSearch(val);
    } else {
      setInternalQuery(val);
    }
  };

  const sortedData = React.useMemo(() => {
    // If sorting is handled externally (and data is likely pre-sorted),
    // we might still want to sort the filtered slice if the user searches within the page.
    // However, usually if external sort is on, we trust the data order.
    // But to be safe and consistent with the visual arrow, we perform the sort on the slice too.
    if (!sortConfig.key) return filteredData;

    return [...filteredData].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      // Simple numeric check
      const aNum = parseFloat(aVal);
      const bNum = parseFloat(bVal);

      if (!isNaN(aNum) && !isNaN(bNum)) {
        aVal = aNum;
        bVal = bNum;
      } else {
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
      }

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortConfig]);

  const handleSort = (key) => {
    if (externalOnSort) {
      externalOnSort(key);
      return;
    }
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setInternalSortConfig({ key, direction });
  };

  return (
    <div className="chart-card p-3 mb-4">

      <div className="d-flex align-items-center mb-3">
        {title && <h6 className="fw-semibold mb-0">{title}</h6>}
        <div className="ms-auto d-flex align-items-center gap-2">
          {filterContent}
          {!hideSearch && (
            <input
              type="text"
              className="form-control form-control-sm"
              style={{ width: 280 }}
              placeholder={searchPlaceholder}
              value={query || ""} // Ensure controlled input
              onChange={handleSearchChange}
            />
          )}
        </div>
      </div>

      <div
        className="table-responsive"
        style={{ maxHeight: `${height}px`, overflowY: "auto" }}
      >
        <table className="table table-hover align-middle small">
          <thead>
            <tr>
              {columns.map((col, i) => (
                <th
                  key={i}
                  onClick={() => col.accessor && handleSort(col.accessor)}
                  style={{ cursor: col.accessor ? 'pointer' : 'default', textAlign: col.align || "left" }}
                >
                  {col.header}
                  {col.accessor && sortConfig.key === col.accessor && (
                    <span className="ms-1">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                  )}
                  {col.accessor && sortConfig.key !== col.accessor && (
                    <span className="text-muted ms-1" style={{ fontSize: '0.7em' }}>↕</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedData.length === 0 ? (
              <tr>
                <td colSpan={Math.max(1, columns.length)} className="text-center text-muted py-5">
                  No records found
                </td>
              </tr>
            ) : (
              sortedData.map((row, i) => (
                <tr
                  key={i}
                  style={{ cursor: onRowClick ? "pointer" : "default" }}
                  onClick={() => onRowClick && onRowClick(row)}
                >
                  {columns.map((col, j) => (
                    <td key={j} style={{ textAlign: col.align || "left" }}>
                      {col.render ? col.render(row[col.accessor], row) : row[col.accessor]}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
